import React from "react";
import { useTranslation } from "react-i18next";
import { motion } from "framer-motion";
import {
  ChevronRightIcon,
  BriefcaseIcon,
  DocumentTextIcon,
  CheckCircleIcon,
  XCircleIcon,
  TrashIcon,
} from "@heroicons/react/24/outline";
import { CountryFlag } from "@/shared/ui";
import { getCountryName } from "@/shared/core";

interface ClientCardProps {
  client: {
    id: string;
    profiles: {
      full_name: string;
      email: string;
      profile_image_url?: string;
      country: string;
    } | null;
    client_type?: string;
    status?: string;
    cases_count?: number;
    documents_count?: number;
  };
  onClick?: () => void;
  onView?: () => void;
  onEdit?: () => void;
  showStats?: boolean;
}

export const ClientCard: React.FC<ClientCardProps> = ({
  client,
  onClick,
  onView,
  showStats = false,
}) => {
  const { t } = useTranslation();

  const getInitials = (name: string) => {
    return name
      .split(" ")
      .map((n) => n[0])
      .join("")
      .toUpperCase()
      .slice(0, 2);
  };

  const getStatusBadge = (status?: string) => {
    if (!status) return null;

    switch (status) {
      case "active":
        return (
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-atlantis-100 text-atlantis-900">
            <CheckCircleIcon className="w-3.5 h-3.5 mr-1" />
            {t("lawyer.clients.status.active")}
          </span>
        );
      case "inactive":
        return (
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-tiber-100 text-tiber-800">
            <XCircleIcon className="w-3.5 h-3.5 mr-1" />
            {t("lawyer.clients.status.inactive")}
          </span>
        );
      case "deleted":
        return (
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-900">
            <TrashIcon className="w-3.5 h-3.5 mr-1" />
            {t("lawyer.clients.status.deleted")}
          </span>
        );
      default:
        return null;
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      whileHover={{ y: -4 }}
      onClick={onView || onClick}
      className="group bg-white rounded-xl p-6 shadow-lg border border-tiber-600 hover:shadow-2xl transition-all duration-300 cursor-pointer"
    >
      {/* Card Header */}
      <div className="flex items-start justify-between mb-4">
        <div className="flex-1">
          <div className="flex items-center gap-3 mb-3">
            {client.profiles?.profile_image_url ? (
              <img
                src={client.profiles.profile_image_url}
                alt={client.profiles.full_name || "Client"}
                className="h-14 w-14 rounded-xl object-cover border-2 border-tiber-100"
              />
            ) : (
              <div className="h-14 w-14 rounded-xl bg-linear-to-br from-tiber-500 to-tiber-500 flex items-center justify-center border-2 border-tiber-100">
                <span className="text-white font-bold text-lg">
                  {getInitials(client.profiles?.full_name || "N/A")}
                </span>
              </div>
            )}
            <div className="flex-1 min-w-0">
              <h3 className="text-xl font-bold text-tiber-900 truncate group-hover:text-atlantis-800 transition-colors">
                {client.profiles?.full_name || "N/A"}
              </h3>
              <p className="text-sm text-tiber-600 truncate mt-1">
                {client.profiles?.email || "N/A"}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            {client.client_type && (
              <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-tiber-100 text-tiber-700">
                {t(`lawyer.clientCard.type.${client.client_type}`)}
              </span>
            )}
            {getStatusBadge(client.status)}
          </div>
        </div>
      </div>

      {/* Card Body */}
      <div className="mb-4">
        <div className="flex items-center gap-2 text-sm text-tiber-600 mb-3">
          {client.profiles?.country && (
            <>
              <CountryFlag
                countryCode={client.profiles.country}
                size="1.2em"
                title={getCountryName(client.profiles.country)}
              />
              <span>{getCountryName(client.profiles.country)}</span>
            </>
          )}
          {!client.profiles?.country && <span>N/A</span>}
        </div>

        {/* Stats Preview */}
        {showStats && (
          <div className="grid grid-cols-2 gap-3 mt-3">
            <div className="flex items-center gap-2 bg-tiber-500/5 rounded-lg p-2">
              <BriefcaseIcon className="h-5 w-5 text-tiber-500" />
              <div>
                <p className="text-xs text-tiber-500 font-medium">
                  {t("lawyer.clientCard.stats.cases")}
                </p>
                <p className="text-sm font-bold text-juridico">
                  {client.cases_count || 0}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-2 bg-tiber-50 rounded-lg p-2">
              <DocumentTextIcon className="h-5 w-5 text-tiber-700" />
              <div>
                <p className="text-xs text-tiber-700 font-medium">
                  {t("lawyer.clientCard.stats.documents")}
                </p>
                <p className="text-sm font-bold text-juridico">
                  {client.documents_count || 0}
                </p>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Card Footer */}
      <div className="flex items-center justify-end pt-4 border-t border-tiber-100">
        <ChevronRightIcon className="h-5 w-5 text-tiber-400 group-hover:text-tiber-500 transition-colors" />
      </div>
    </motion.div>
  );
};
